import * as React from 'react';

/**
 * Panel to announce a new feature/update pushed by customer.io.
 *
 * Important: this component is always mounted so that at any time it can get replaced
 * remotely by an iframe panel served by customer.io.
 */
export function CustomerIOAlert(props: {
    onBannerAppears?: () => void | Promise<void>;
}): React.ReactElement | null {
    const alertRef = React.useRef<HTMLDivElement>(null);
    useCustomerIOAlertBannerAppeared(alertRef, props.onBannerAppears);

    return (
        <div
            ref={alertRef}
            id="customerio-alert"
            className="relative overflow-hidden rounded-md bg-transparent shadow empty:hidden [&_iframe]:block [&_iframe]:w-full"
        />
    );
}

/**
 * Detect when Customer.io injects a banner into the alert slot.
 * Customer.io owns this DOM mutation outside React, so we observe the slot until
 * the first direct child appears, report it once, and immediately disconnect.
 *
 * Usage example: this is used by the site-centric confirm dialog to preload its image so it
 * displays instantly when the dialog is opened.
 */
function useCustomerIOAlertBannerAppeared(
    alertRef: React.RefObject<HTMLDivElement | null>,
    onBannerAppears?: () => void | Promise<void>
) {
    const hasReportedBanner = React.useRef(false);

    React.useEffect(() => {
        const alert = alertRef.current;

        if (!alert || !onBannerAppears || hasReportedBanner.current) {
            return;
        }

        let observer: MutationObserver | null = null;
        const reportBanner = () => {
            if (!alert.hasChildNodes()) {
                return;
            }

            hasReportedBanner.current = true;
            observer?.disconnect();
            observer = null;
            void Promise.resolve(onBannerAppears()).catch(() => undefined);
        };

        reportBanner();

        if (!alert.hasChildNodes()) {
            observer = new MutationObserver(reportBanner);
            observer.observe(alert, { childList: true });
        }

        return () => {
            observer?.disconnect();
        };
    }, [alertRef, onBannerAppears]);
}
